import { ExportFrequency, type Grid } from "@/API";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getRestaurantsInGrid,
  getRestaurantsInGridState,
  resetRestaurantsInGridState,
} from "@/store/getRestaurantsInGridState";
import {
  resetupdateGridState,
  updateGrid,
  updateGridState,
} from "@/store/updateGridState";
import { SelectGroup } from "@radix-ui/react-select";
import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grid: Grid;
};

const DialogUpdatePlanification: FC<Props> = ({ open, onOpenChange, grid }) => {
  const { t } = useTranslation();

  const { data: restaurants, isLoading: isRestaurantsLoading } =
    getRestaurantsInGridState();
  const { data: updatedGrid, isLoading: isUpdatingGrid } = updateGridState();

  const [exportFrequency, setExportFrequency] = useState<ExportFrequency>(
    grid?.frequency ?? ExportFrequency.ONE_WEEK,
  );
  const [emailInput, setEmailInput] = useState<string>(
    grid?.emails?.join(",") ?? "",
  );
  const [removePlatification, setRemovePlanification] = useState(false);

  useEffect(() => {
    if (grid.id) getRestaurantsInGrid({ gridId: grid.id });

    return () => {
      resetRestaurantsInGridState();
    };
  }, [grid]);

  useEffect(() => {
    if (isUpdatingGrid) {
      if (!removePlatification)
        toast(t("updating_planification"), {
          duration: 2000,
        });
      if (removePlatification)
        toast(t("deleting_planification"), {
          duration: 2000,
        });
    }
  }, [isUpdatingGrid, removePlatification]);

  useEffect(() => {
    if (isUpdatingGrid) {
      if (!removePlatification)
        toast.success(t("planification_updated"), {
          duration: 2000,
        });
      if (removePlatification)
        toast.success(t("planification_deleted"), {
          duration: 2000,
        });
      onOpenChange(false);
    }

    return () => {
      resetupdateGridState();
    };
  }, [isUpdatingGrid, updatedGrid]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader></DialogHeader>
        <DialogDescription className="flex flex-col gap-[24px]">
          <div className="flex flex-col gap-[12px]">
            <Text fontSize={16} fontWeight={500} color="#414651">
              {t("cadence")}
            </Text>
            <Select
              value={exportFrequency}
              onValueChange={(value) =>
                setExportFrequency(value as ExportFrequency)
              }
            >
              <SelectTrigger className="w-[100%]">
                <SelectValue className="w-[100%]" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ExportFrequency.ONE_WEEK}>
                    {t("weekly")}
                  </SelectItem>
                  <SelectItem value={ExportFrequency.ONE_MONTH}>
                    {t("monthly")}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[12px]">
            <Text fontSize={16} fontWeight={500} color="#414651">
              {t("recipients")}
            </Text>
            <div className="flex flex-col gap-[8px]">
              <Input
                placeholder="hello1@gmail.com,hello2@gmail.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
              <Text fontWeight={400} fontSize={14} color="#44546F">
                {t("export_recipients_helper_text")}
              </Text>
            </div>
          </div>
        </DialogDescription>
        <DialogFooter className="flex sm:justify-start mt-[12px]">
          <Button
            disabled={isRestaurantsLoading}
            variant="secondaryFilled"
            onClick={() => {
              if (restaurants)
                updateGrid({
                  grid: {
                    ...grid,
                    frequency: null,
                    emails: [],
                  },
                  restaurantsId:
                    restaurants.data
                      .filter((r) => r !== null)
                      .map((r) => r.id) ?? [],
                });
              setRemovePlanification(true);
            }}
          >
            {t("delete_planification")}
          </Button>
          <Button
            style={{ width: "calc(100% - 210px)" }}
            disabled={isRestaurantsLoading}
            onClick={() => {
              if (restaurants)
                updateGrid({
                  grid: {
                    ...grid,
                    frequency: exportFrequency,
                    emails: emailInput.split(",").map((email) => email.trim()),
                  },
                  restaurantsId:
                    restaurants.data
                      .filter((r) => r !== null)
                      .map((r) => r.id) ?? [],
                });
            }}
          >
            {t("edit_planification")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogUpdatePlanification;
